/**
 * 정렬·필터 상태 hook.
 *
 *   const cs = useColState();
 *   const cs = useColState({ storageKey: 'purchase-list', initialSort: [{key:'lastUpdate', dir:'desc'}] });
 *   cs.toggleSort('salesId');   // asc → desc → 해제 cycle (1차 키일 때)
 *                                // 다른 키 클릭 시 → 그 키를 1차로 이동 (asc)
 *   cs.setSort('price', 'desc'); // 단일 정렬 (멀티 정렬 reset)
 *   cs.setFilter('status', new Set(['발주','납품']));
 *   cs.clearFilter('status');
 *   cs.resetAll();
 *
 * 다단계 정렬 (2026-05-14):
 *   - 새 키 클릭 = 그 키를 sortStack 의 1차 위치로 (기존 정렬은 2차/3차 로 밀림)
 *   - 1차 키 재클릭 = asc → desc → 해제
 *   - 예: 제안가 클릭 → 거래처 클릭 → 거래처 1차 + 제안가 2차로 정렬됨
 *
 * 세션 영구화 (옵션, 2026-05-14):
 *   - storageKey 제공 시 sessionStorage 에 sortStack + filters 저장 → 같은 세션
 *     내 메뉴 이동 시 상태 유지. resetAll 은 storage 도 비움.
 *   - filters 는 Set<string> 이라 JSON 직렬화 시 배열로 변환.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ColState, SortSpec } from './types';

export type ColStateApi = ColState & {
  toggleSort: (key: string) => void;
  setSort: (key: string, dir: 'asc' | 'desc' | null) => void;
  setFilter: (key: string, allowed: Set<string>) => void;
  clearFilter: (key: string) => void;
  resetAll: () => void;
};

export interface UseColStateOptions {
  storageKey?: string;
  initialSort?: SortSpec[];
}

type PersistShape = {
  sortStack: SortSpec[];
  filters: Record<string, string[]>; // Set → array 직렬화
};

function loadPersisted(key: string): PersistShape | null {
  try {
    const raw = sessionStorage.getItem(`colstate/${key}`);
    if (!raw) return null;
    return JSON.parse(raw) as PersistShape;
  } catch {
    return null;
  }
}

function savePersisted(
  key: string,
  sortStack: SortSpec[],
  filters: Record<string, Set<string>>,
): void {
  try {
    const filtersJson: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(filters)) filtersJson[k] = [...v];
    sessionStorage.setItem(
      `colstate/${key}`,
      JSON.stringify({ sortStack, filters: filtersJson } satisfies PersistShape),
    );
  } catch {
    /* QuotaExceeded 등 무시 */
  }
}

export function useColState(opts: UseColStateOptions = {}): ColStateApi {
  const { storageKey, initialSort } = opts;

  const [sortStack, setSortStack] = useState<SortSpec[]>(() => {
    if (storageKey) {
      const p = loadPersisted(storageKey);
      if (p) return p.sortStack;
    }
    return initialSort ?? [];
  });
  const [filters, setFilters] = useState<Record<string, Set<string>>>(() => {
    if (storageKey) {
      const p = loadPersisted(storageKey);
      if (p) {
        const out: Record<string, Set<string>> = {};
        for (const [k, v] of Object.entries(p.filters)) out[k] = new Set(v);
        return out;
      }
    }
    return {};
  });

  // sortStack/filters 변경 시 storage 동기화 (useEffect → stale closure 회피).
  // resetAll 직후 storage 가 다시 빈 값으로 덮어쓰여도 무방 (== 초기 상태).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!storageKey) return;
    if (!didMountRef.current) {
      // 초기 렌더 후 mount — 이미 load 된 값이라 다시 저장할 필요 X
      didMountRef.current = true;
      return;
    }
    savePersisted(storageKey, sortStack, filters);
  }, [storageKey, sortStack, filters]);

  const toggleSort = useCallback((key: string) => {
    setSortStack(prev => {
      const first = prev[0];
      if (first && first.key === key) {
        // 1차 키 cycle: asc → desc → 해제
        if (first.dir === 'asc') return [{ key, dir: 'desc' as const }, ...prev.slice(1)];
        return prev.slice(1); // desc → 해제
      }
      // 다른 위치에 있던 키 or 신규 → 1차로 이동/추가 (asc 부터)
      const filtered = prev.filter(s => s.key !== key);
      return [{ key, dir: 'asc' as const }, ...filtered];
    });
  }, []);

  const setSort = useCallback((key: string, dir: 'asc' | 'desc' | null) => {
    if (dir === null) setSortStack([]);
    else setSortStack([{ key, dir }]);
  }, []);

  const setFilter = useCallback((key: string, allowed: Set<string>) => {
    setFilters(prev => ({ ...prev, [key]: allowed }));
  }, []);

  const clearFilter = useCallback((key: string) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setSortStack([]);
    setFilters({});
    if (storageKey) {
      try {
        sessionStorage.removeItem(`colstate/${storageKey}`);
      } catch {
        /* ignore */
      }
    }
  }, [storageKey]);

  // 1차 키/방향은 stack 첫 항목 alias (backwards-compat)
  const sortKey = sortStack[0]?.key ?? null;
  const sortDir = sortStack[0]?.dir ?? null;

  return {
    sortKey,
    sortDir,
    sortStack,
    filters,
    toggleSort,
    setSort,
    setFilter,
    clearFilter,
    resetAll,
  };
}
